import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import QRCode from 'react-qr-code';
import classNames from 'classnames';
import moment from 'moment';
import _ from 'lodash';

import {
  getEditOrder,
  getPollingEditOrder,
  isLoadingOrderSelector,
  selectedOrderSelector,
  updateOrderStatus,
} from 'src/store/orders';

import { CustomButton, CustomInput, PageLoader, WhiteCard } from 'src/components';

import { ReactComponent as CopyIcon } from 'src/assets/exchange/CopyIcon.svg';
import './viewExchange.scss';

export const ViewExchange = () => {
  const { t, i18n } = useTranslation('viewExchange');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const selectedOrder = useSelector(selectedOrderSelector);
  const isLoading = useSelector(isLoadingOrderSelector);

  const [remainingTime, setRemainingTime] = useState('');

  useEffect(() => {
    const payload = {
      id: params?.id,
      navigate,
    };

    dispatch(getEditOrder(payload));
  }, [dispatch, navigate, params?.id, i18n.language]);

  useEffect(() => {
    const createdTime = moment(selectedOrder?.created_at);
    const maxPayTime = createdTime.add(selectedOrder?.time_to_pay, 'minutes');

    const updateCountdown = () => {
      const now = moment();
      const diff = moment.duration(maxPayTime.diff(now));

      if (diff.asMilliseconds() <= 0) {
        setRemainingTime('00:00:00');
        clearInterval(timer);

        setTimeout(() => {
          const payload = {
            id: params?.id,
            navigate,
          };

          dispatch(getEditOrder(payload));
        }, 5000);
      } else {
        const hours = Math.floor(diff.asHours());
        const minutes = diff.minutes();
        const seconds = diff.seconds();
        setRemainingTime(
          `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
            seconds,
          ).padStart(2, '0')}`,
        );
      }
    };

    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, [dispatch, navigate, params?.id, selectedOrder?.created_at, selectedOrder?.time_to_pay]);

  useEffect(() => {
    if (!_.isEmpty(selectedOrder)) {
      const payload = {
        id: params?.id,
        navigate,
      };

      const interval = setInterval(() => {
        dispatch(getPollingEditOrder(payload));
      }, 3000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [dispatch, navigate, params?.id, selectedOrder]);

  const onClickCancelOrder = useCallback(() => {
    const payload = {
      id: params?.id,
      payload: {
        status: 'CANCEL',
      },
      navigate,
    };

    dispatch(updateOrderStatus(payload));
  }, [dispatch, navigate, params?.id]);

  const onClickCopy = useCallback(
    (value) => () => {
      toast(t('copied'), { type: 'success' });

      navigator.clipboard.writeText(value);
    },
    [t],
  );

  const renderStyleByStatus = useCallback((status) => {
    const style = classNames({
      pending: status?.toLowerCase() === 'pending' || status?.toLowerCase() === 'partial_payment',
      paid: status?.toLowerCase() === 'paid',
      complete: status?.toLowerCase() === 'complete',
      cancel: status?.toLowerCase() === 'cancel',
    });

    return style;
  }, []);

  const renderItem = useCallback(
    (text, value, key) => {
      return (
        <div className="view-exchange__item" key={key}>
          <div className="view-exchange__item__text">{key ? text : t(text)}</div>
          <h5>{value}</h5>
        </div>
      );
    },
    [t],
  );

  const headerTitle = {
    PENDING: t('exchangeStatuses:PENDING'),
    PAID: t('exchangeStatuses:PAID'),
    COMPLETE: t('exchangeStatuses:COMPLETE'),
    CANCEL: t('exchangeStatuses:CANCEL'),
    PARTIAL_PAYMENT: t('exchangeStatuses:PARTIAL_PAYMENT'),
  };

  const bodyTitle = {
    PENDING: t('statuses:PENDING'),
    PAID: t('statuses:PAID'),
    COMPLETE: t('statuses:COMPLETE'),
    CANCEL: t('statuses:CANCEL'),
    PARTIAL_PAYMENT: t('statuses:PARTIAL_PAYMENT'),
  };

  const given = selectedOrder?.given;
  const received = selectedOrder?.received;

  return (
    <div className="view-exchange">
      {isLoading ? <PageLoader /> : null}
      <WhiteCard>
        <h2>
          {t('view')} {selectedOrder?.id}
        </h2>
        <div className="view-exchange__content">
          <WhiteCard>
            <h5 className={renderStyleByStatus(selectedOrder?.status)}>
              {headerTitle[selectedOrder?.status]}
            </h5>{' '}
            {renderItem('created', moment(selectedOrder?.created_at).format('DD.MM.YYYY, HH:mm'))}
            {renderItem(
              'status',
              <span className={renderStyleByStatus(selectedOrder?.status)}>
                {bodyTitle[selectedOrder?.status]}
              </span>,
            )}
            {renderItem(
              'exchangeRate',
              `${given?.rate_given_currency} ${given?.code} → ${received?.rate_received_currency} ${received?.code}`,
            )}
            {/* {renderItem('exchangeBonus', mockedData.exchangeBonus)} */}
            {renderItem(
              'createExchange:give',
              `${given?.name}, ${selectedOrder?.given_sum} ${given?.code}`,
            )}
            {renderItem(
              'createExchange:receive',
              `${received?.name}, ${selectedOrder?.received_sum} ${received?.code}`,
            )}
            {selectedOrder?.fields?.map((item) =>
              renderItem(item?.field_translate, item?.field_name, item?.field),
            )}
          </WhiteCard>
          <div className="view-exchange__right">
            <div className="view-exchange__right__header">
              <h4>{t('title:createRequest')}</h4>
              <CustomButton
                title={t('title:cancelRequest')}
                variant="filled"
                color="red"
                onClick={onClickCancelOrder}
              />
            </div>
            <WhiteCard>
              <div className="view-exchange__text">
                <span>{t('send')}</span>
                <strong>
                  {selectedOrder?.given_sum} {given?.code}
                </strong>
                <span>{t('wait')}</span>
              </div>
              <div className="view-exchange__qr">
                <QRCode value={selectedOrder?.transaction?.name || ''} size={150} />
              </div>
            </WhiteCard>
            <div className="view-exchange__input">
              <CustomInput label={t('toPay')} value={selectedOrder?.given_sum} disabled />
              <CustomButton icon={<CopyIcon />} onClick={onClickCopy(selectedOrder?.given_sum)} />
            </div>
            <div className="view-exchange__input">
              <CustomInput
                label={t('addressToPay')}
                value={selectedOrder?.transaction?.name}
                disabled
              />
              <CustomButton
                icon={<CopyIcon />}
                onClick={onClickCopy(selectedOrder?.transaction?.name)}
              />
            </div>
            {selectedOrder?.transaction?.memo ? (
              <div className="view-exchange__input">
                <CustomInput
                  label={t('currenciesForm:memo')}
                  value={selectedOrder?.transaction?.memo}
                  disabled
                />
                <CustomButton
                  icon={<CopyIcon />}
                  onClick={onClickCopy(selectedOrder?.transaction?.memo)}
                />
              </div>
            ) : null}
            <div className="view-exchange__text__sub">{t('weCheck')}</div>
            <div className="view-exchange__text" style={{ textAlign: 'center' }}>
              <div className="view-exchange__text__time">
                {t('timeToPay')} <span className="view-exchange__text__timer">{remainingTime}</span>
              </div>
            </div>
            {/* <div className="view-exchange__warning">
              <div>
                <img src={WarningIcon} alt={WarningIcon} />
              </div>
              {t('warning')}
            </div> */}
          </div>
        </div>
      </WhiteCard>
    </div>
  );
};
